import { useMemo, useState } from 'react';
import {
  Badge,
  Input,
  MessageBar,
  MessageBarBody,
  Slider,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { daysSinceSignIn, formatDateTime, formatValidity, stateColor } from '../features/guests/guest-format';
import '../features/guests/guests.css';
import type { GuestSummaryDto } from '../api/types';

type SortKey = 'name' | 'company' | 'mail' | 'lastSignIn' | 'validUntil' | 'state';
/** Welche Kennzahl die Liste gerade einschränkt. */
type SignInFilter = 'none' | 'recent' | 'stale';

const RECENT_DEFAULT_DAYS = 30;
const STALE_DEFAULT_DAYS = 90;

function Kpi({
  label,
  value,
  tone,
  pressed,
  disabled,
  onClick,
}: {
  label: string;
  value: string;
  tone?: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <div className="guest-kpi-label">{label}</div>
      <div className="guest-kpi-value" style={{ color: tone }}>
        {value}
      </div>
    </>
  );

  // Nur die Anmelde-Kennzahlen sind als Filter schaltbar — die übrigen bleiben
  // reine Anzeige, damit die Bedienung eindeutig bleibt.
  return onClick ? (
    <button
      type="button"
      className="guest-kpi guest-kpi-toggle"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      title={disabled ? 'Ohne Anmelde-Aktivität nicht auswertbar.' : 'Liste auf diese Gäste einschränken'}
    >
      {body}
    </button>
  ) : (
    <div className="guest-kpi">{body}</div>
  );
}

export function GuestsOverviewPage() {
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.listGuests(), [api]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 });
  const [recentDays, setRecentDays] = useState(RECENT_DEFAULT_DAYS);
  const [staleDays, setStaleDays] = useState(STALE_DEFAULT_DAYS);
  const [signInFilter, setSignInFilter] = useState<SignInFilter>('none');

  const signInAvailable = data?.signInAvailable ?? false;

  // Anmelde-Alter einmal je Gast bestimmen — Kennzahlen und Filter teilen sich
  // dieselbe Grundlage. null = nie angemeldet.
  const withAge = useMemo(
    () => (data?.guests ?? []).map((g) => ({ guest: g, age: daysSinceSignIn(g.lastSignIn) })),
    [data],
  );

  const isRecent = (age: number | null) => age !== null && age <= recentDays;
  // „Nie angemeldet" zählt als inaktiv — genau diese Gäste sind beim Aufräumen gemeint.
  const isStale = (age: number | null) => age === null || age > staleDays;

  const recentCount = useMemo(() => withAge.filter((x) => isRecent(x.age)).length, [withAge, recentDays]);
  const staleCount = useMemo(() => withAge.filter((x) => isStale(x.age)).length, [withAge, staleDays]);

  const rows = useMemo(() => {
    let r = withAge;

    if (signInAvailable && signInFilter === 'recent') r = r.filter((x) => isRecent(x.age));
    if (signInAvailable && signInFilter === 'stale') r = r.filter((x) => isStale(x.age));

    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter(
        ({ guest: g }) =>
          g.displayName.toLowerCase().includes(q) ||
          (g.company ?? '').toLowerCase().includes(q) ||
          g.mail.toLowerCase().includes(q),
      );
    }

    const val = (g: GuestSummaryDto): number | string => {
      switch (sort.key) {
        case 'company':
          return (g.company ?? '').toLowerCase();
        case 'mail':
          return g.mail.toLowerCase();
        // Nie angemeldet bzw. unbefristet sortiert ans Ende der Zeitspalten.
        case 'lastSignIn':
          return g.lastSignIn ? new Date(g.lastSignIn).getTime() : 0;
        case 'validUntil':
          return g.validUntilUtc ? new Date(g.validUntilUtc).getTime() : Number.MAX_SAFE_INTEGER;
        case 'state':
          return g.state;
        default:
          return g.displayName.toLowerCase();
      }
    };
    return [...r].sort((x, y) => {
      const a = val(x.guest);
      const b = val(y.guest);
      return (a < b ? -1 : a > b ? 1 : 0) * sort.dir;
    });
  }, [withAge, search, sort, signInFilter, signInAvailable, recentDays, staleDays]);

  function head(key: SortKey, label: string, width: string) {
    const active = sort.key === key;
    return (
      <TableHeaderCell
        style={{ cursor: 'pointer', width }}
        onClick={() => setSort((s) => ({ key, dir: s.key === key ? ((s.dir === 1 ? -1 : 1) as 1 | -1) : 1 }))}
      >
        {label}
        {active ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
      </TableHeaderCell>
    );
  }

  /** Filter umschalten — erneuter Klick auf dieselbe Kachel hebt ihn auf. */
  const toggleFilter = (f: SignInFilter) => () => setSignInFilter((current) => (current === f ? 'none' : f));

  return (
    <div>
      <PageHeader
        title="Gast-Übersicht"
        description="Alle Gast-Einladungen des Tenants mit Firma, Adresse, letzter Anmeldung und Gültigkeit."
      />

      {loading ? <Spinner label="Lade Gast-Einladungen…" /> : null}
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error.message}</MessageBarBody>
        </MessageBar>
      ) : null}

      {data ? (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: tokens.spacingVerticalM }}>
            <Kpi label="Gäste gesamt" value={data.total.toLocaleString('de-DE')} />
            <Kpi
              label="Einladung offen"
              value={String(data.pendingCount)}
              tone={data.pendingCount > 0 ? tokens.colorPaletteYellowForeground1 : undefined}
            />
            <Kpi
              label={`Angemeldet (≤ ${recentDays} T)`}
              value={signInAvailable ? String(recentCount) : 'n/v'}
              tone={signInAvailable ? tokens.colorPaletteGreenForeground1 : undefined}
              pressed={signInFilter === 'recent'}
              disabled={!signInAvailable}
              onClick={toggleFilter('recent')}
            />
            <Kpi
              label={`Inaktiv (> ${staleDays} T)`}
              value={signInAvailable ? String(staleCount) : 'n/v'}
              tone={signInAvailable && staleCount > 0 ? tokens.colorPaletteRedForeground1 : undefined}
              pressed={signInFilter === 'stale'}
              disabled={!signInAvailable}
              onClick={toggleFilter('stale')}
            />
            <Kpi
              label="Läuft bald ab (≤14 T)"
              value={String(data.expiringSoonCount)}
              tone={data.expiringSoonCount > 0 ? tokens.colorPaletteYellowForeground1 : undefined}
            />
            <Kpi
              label="Abgelaufen"
              value={String(data.expiredCount)}
              tone={data.expiredCount > 0 ? tokens.colorPaletteRedForeground1 : undefined}
            />
          </div>

          {signInAvailable ? (
            <div className="guest-scales" style={{ marginBottom: tokens.spacingVerticalM }}>
              <div className="guest-scale">
                <span className="guest-scale-label">Angemeldet innerhalb</span>
                <Slider min={1} max={180} step={1} value={recentDays} onChange={(_, d) => setRecentDays(d.value)} />
                <span className="guest-scale-value">{recentDays} T</span>
              </div>
              <div className="guest-scale">
                <span className="guest-scale-label">Inaktiv seit mehr als</span>
                <Slider min={30} max={730} step={5} value={staleDays} onChange={(_, d) => setStaleDays(d.value)} />
                <span className="guest-scale-value">{staleDays} T</span>
              </div>
            </div>
          ) : (
            <MessageBar intent="info" style={{ marginBottom: tokens.spacingVerticalM }}>
              <MessageBarBody>
                Die letzte Anmeldung konnte nicht gelesen werden — dafür sind „AuditLog.Read.All" und Entra ID P1 nötig.
                Die Anmelde-Kennzahlen und ihre Filter bleiben deshalb ohne Wert.
              </MessageBarBody>
            </MessageBar>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacingHorizontalM,
              marginBottom: tokens.spacingVerticalM,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ maxWidth: 360, flex: '1 1 260px' }}>
              <Input
                placeholder="Nach Name, Firma oder Mail suchen…"
                value={search}
                onChange={(_, d) => setSearch(d.value)}
                contentBefore={<SearchRegular />}
                contentAfter={
                  search ? (
                    <DismissRegular
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSearch('')}
                      aria-label="Suche leeren"
                    />
                  ) : undefined
                }
              />
            </div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {rows.length} von {data.total} angezeigt
              {signInFilter === 'recent' ? ` · gefiltert: angemeldet ≤ ${recentDays} Tage` : ''}
              {signInFilter === 'stale' ? ` · gefiltert: inaktiv > ${staleDays} Tage (inkl. nie angemeldet)` : ''}
            </Text>
          </div>

          {rows.length === 0 ? (
            <Text>Keine Gast-Einladungen gefunden.</Text>
          ) : (
            <div className="guest-table-wrap">
              <Table size="small" aria-label="Gast-Einladungen" className="guest-table">
                <TableHeader>
                  <TableRow>
                    {head('name', 'Name', '19%')}
                    {head('company', 'Firma', '15%')}
                    {head('mail', 'Mail', '25%')}
                    {head('lastSignIn', 'Letzte Anmeldung', '15%')}
                    {head('validUntil', 'Gültig bis', '15%')}
                    {head('state', 'Status', '11%')}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(({ guest: g, age }) => (
                    <TableRow key={g.id}>
                      <TableCell>
                        <span className="pii">{g.displayName}</span>
                      </TableCell>
                      <TableCell>{g.company ?? '—'}</TableCell>
                      <TableCell>
                        <span className="pii">{g.mail || '—'}</span>
                      </TableCell>
                      <TableCell style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {!signInAvailable ? 'n/v' : age === null ? 'nie' : formatDateTime(g.lastSignIn)}
                      </TableCell>
                      <TableCell style={{ fontVariantNumeric: 'tabular-nums' }}>{formatValidity(g)}</TableCell>
                      <TableCell>
                        <Badge appearance="tint" color={stateColor(g.state)}>
                          {g.state}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
